import { useState, useEffect, useRef } from "react";
import {
  IcReceipt, IcEdit, IcRefresh, IcCard, IcLock, IcUnlock, IcCheck, IcPrinter,
  IcWarning, IcClipboard, IcBolt,
} from "./Icons";
// Split/move için basit çift-yönlü ok
const IcSwap = (p) => (
  <svg width={p.size ?? 14} height={p.size ?? 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 7h11l-3-3M17 17H6l3 3" />
  </svg>
);

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

/* ── Event meta-map ──────────────────────────────────────────── */
const EVENT_META = {
  ORDER_CREATED:          { Icon: IcReceipt,   label: "Sipariş",         cls: "ev-green"   },
  ORDER_EDITED:           { Icon: IcEdit,      label: "Düzenlendi",      cls: "ev-amber"   },
  ORDER_STATUS_CHANGED:   { Icon: IcRefresh,   label: "Durum",            cls: "ev-blue"    },
  PAYMENT_PROCESSED:      { Icon: IcCard,      label: "Ödeme",            cls: "ev-teal"    },
  SESSION_CLOSED:         { Icon: IcLock,      label: "Kapandı",          cls: "ev-neutral" },
  SESSION_REOPENED:       { Icon: IcUnlock,    label: "Yeniden Açıldı",  cls: "ev-rose"    },
  SESSION_MOVED_TABLE:    { Icon: IcSwap,      label: "Taşındı",          cls: "ev-amber"   },
  RECEIPT_AUTO_PRINTED:   { Icon: IcPrinter,   label: "Adisyon (Oto.)",  cls: "ev-blue"    },
  RECEIPT_MANUAL_PRINTED: { Icon: IcPrinter,   label: "Adisyon",         cls: "ev-blue"    },
  OVERPAYMENT_DETECTED:   { Icon: IcWarning,   label: "Fazla Ödeme",     cls: "ev-red"     },
};
const DEFAULT_META = { Icon: IcClipboard, label: "Olay", cls: "ev-neutral" };

/*
 * PAYMENT_PROCESSED için dinamik badge meta'sı.
 *
 * Öncelik sırası:
 *  1. status=CLOSED   → "Tam Ödeme"   (session kapandı, bu son işlem)
 *  2. split_index/total var → "Parçalı (X/Y)"
 *  3. status=PARTIALLY_PAID → "Parçalı Ödeme"
 *  4. default → "Ödeme"
 *
 * NOT: SESSION_FULLY_PAID eventi feed'den filtrelendiği için
 * son ödeme etiketi buradaki "Tam Ödeme" üzerinden gösterilir.
 */
function paymentMeta(pl) {
  const base = EVENT_META.PAYMENT_PROCESSED;
  if (pl?.status === "CLOSED") {
    return { ...base, Icon: IcCheck, label: "Tam Ödeme", cls: "ev-mint" };
  }
  if (pl?.split_index && pl?.split_total) {
    return { ...base, label: `Parçalı (${pl.split_index}/${pl.split_total})` };
  }
  if (pl?.status === "PARTIALLY_PAID") {
    return { ...base, label: "Parçalı Ödeme" };
  }
  return base;
}

/* ── Helpers ────────────────────────────────────────────────── */
function fmtTime(ts) {
  if (!ts) return "--:--";
  // DB UTC timestamp → UTC+3 (Turkey)
  const d = new Date(String(ts).replace(" ", "T") + "Z");
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" });
}

function extractTable(type, pl) {
  if (!pl) return null;
  if (type === "SESSION_MOVED_TABLE") return null; // desc zaten from→to gösteriyor
  return pl.table_label || pl.table || null;
}

function extractDesc(type, pl) {
  if (!pl) return "";
  switch (type) {
    case "ORDER_CREATED":
      return pl.total != null ? `₺${Math.round(pl.total)}` : "";
    case "ORDER_EDITED":
      return pl.new_total != null ? `₺${Math.round(pl.new_total)}` : "";
    case "PAYMENT_PROCESSED": {
      const m = pl.method === "CASH" ? "Nakit" : pl.method === "CARD" ? "Kart" : "";
      return pl.amount != null ? `₺${Math.round(pl.amount)}${m ? " · " + m : ""}` : "";
    }
    case "RECEIPT_AUTO_PRINTED":
    case "RECEIPT_MANUAL_PRINTED":
      return pl.total_amount != null ? `₺${Math.round(pl.total_amount)}` : "";
    case "ORDER_STATUS_CHANGED":
      return [pl.from_status, pl.to_status].filter(Boolean).join(" → ");
    case "OVERPAYMENT_DETECTED":
      return pl.paid_amount != null ? `₺${Math.round(pl.paid_amount)}` : "";
    case "SESSION_MOVED_TABLE":
      return [pl.from_table, pl.to_table].filter(Boolean).join(" → ");
    default:
      return "";
  }
}

/* ── Main component ─────────────────────────────────────────── */
export default function LiveFeed({ onViewSession }) {
  const [events,  setEvents]  = useState([]);
  const [open,    setOpen]    = useState(true);
  const [pulse,   setPulse]   = useState(false);
  const prevTopId             = useRef(null);

  useEffect(() => {
    doFetch();
    const iv = setInterval(doFetch, 5000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  /*
   * SESSION_FULLY_PAID eventi feed'de gösterilmez:
   *   - total_amount (oturum toplamı) içerir, işlem tutarını değil.
   *   - Son ödeme zaten PAYMENT_PROCESSED{status:CLOSED} = "Tam Ödeme" olarak gelir.
   * Filtreleme kayıp yaratmasın diye 2× limit çekiyoruz.
   */
  const FEED_HIDDEN = new Set(["SESSION_FULLY_PAID"]);

  const doFetch = async () => {
    try {
      const res = await fetch(`${API}/api/admin/events?limit=14`, {
        credentials: "include",
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) return;
      const data = await res.json();
      const seenIds = new Set();
      const evs  = (data.events || [])
        .filter(e => !FEED_HIDDEN.has(e.type))
        .filter(e => { if (seenIds.has(e.id)) return false; seenIds.add(e.id); return true; })
        .slice(0, 7);

      /* Yeni olay gelince toggle'ı pulse yap */
      if (evs.length && evs[0].id !== prevTopId.current) {
        if (prevTopId.current !== null) {
          setPulse(true);
          setTimeout(() => setPulse(false), 700);
        }
        prevTopId.current = evs[0].id;
      }
      setEvents(evs);
    } catch (_) {}
  };

  const handleRow = (ev) => {
    const sid = ev.payload?.session_id;
    if (sid && onViewSession) onViewSession(sid);
  };

  return (
    <div className="live-feed">

      {/* ── Panel ── */}
      {open && (
        <div className="lf-panel">

          {/* Header */}
          <div className="lf-hdr">
            <span className="lf-hdr-dot" />
            <span className="lf-hdr-title">Canlı Akış</span>
            <button
              className="lf-hdr-min"
              onClick={() => setOpen(false)}
              title="Küçült"
            >╌</button>
          </div>

          {/* Event rows */}
          <div className="lf-list">
            {events.length === 0 ? (
              <div className="lf-empty">Henüz işlem yok</div>
            ) : (
              events.map((ev, i) => {
                const meta  = ev.type === "PAYMENT_PROCESSED"
                  ? paymentMeta(ev.payload)
                  : (EVENT_META[ev.type] || DEFAULT_META);
                const table = extractTable(ev.type, ev.payload);
                const desc  = extractDesc(ev.type, ev.payload);
                const sid   = ev.payload?.session_id;
                return (
                  <div
                    key={ev.id}
                    className={[
                      "lf-row",
                      sid          ? "lf-clickable" : "",
                      i === 0      ? "lf-row-new"   : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleRow(ev)}
                    title={sid ? "Masayı aç" : undefined}
                  >
                    <span className="lf-time">{fmtTime(ev.created_at)}</span>
                    <span className={`lf-badge ${meta.cls}`}>
                      <meta.Icon size={12} /> {meta.label}
                    </span>
                    <div className="lf-row-info">
                      {table && <span className="lf-table">{table}</span>}
                      {desc  && <span className="lf-desc">{desc}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}

      {/* ── Floating toggle button ── */}
      <button
        className={`lf-toggle${pulse ? " lf-toggle-pulse" : ""}`}
        onClick={() => setOpen(o => !o)}
        title={open ? "Küçült" : "Canlı Akışı Aç"}
      >
        <IcBolt size={22} />
        {!open && events.length > 0 && (
          <span className="lf-toggle-badge">{events.length}</span>
        )}
      </button>

    </div>
  );
}
